import { useState, useEffect } from 'react';
import { dashboardService } from '../services/api';
import { DashboardStats, RecentReview, PopularProduct } from '../types/dashboard';
import { useAuth } from '../context/AuthContext';

interface UseDashboardDataReturn {
  stats: DashboardStats | null;
  recentReviews: RecentReview[];
  popularProducts: PopularProduct[];
  loadingStats: boolean;
  loadingReviews: boolean;
  loadingProducts: boolean;
  errorStats: string | null;
  errorReviews: string | null;
  errorProducts: string | null;
  refetchStats: () => void;
  refetchReviews: () => void;
  refetchProducts: () => void;
}

export const useDashboardData = (placeId: string): UseDashboardDataReturn => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentReviews, setRecentReviews] = useState<RecentReview[]>([]);
  const [popularProducts, setPopularProducts] = useState<PopularProduct[]>([]);
  
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  const [errorStats, setErrorStats] = useState<string | null>(null);
  const [errorReviews, setErrorReviews] = useState<string | null>(null);
  const [errorProducts, setErrorProducts] = useState<string | null>(null);

  const { validateAuth } = useAuth();

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      setErrorStats(null);
      const data = await dashboardService.getStats(placeId);
      setStats(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Se for 401, validar a autenticação
        const isValid = await validateAuth();
        if (!isValid) {
          setErrorStats('Sessão expirada. Faça login novamente.');
        } else {
          setErrorStats('Erro ao carregar estatísticas');
        }
      } else {
        setErrorStats('Erro ao carregar estatísticas');
      }
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      setErrorReviews(null);
      const data = await dashboardService.getRecentReviews(placeId, 5);
      setRecentReviews(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Se for 401, validar a autenticação
        const isValid = await validateAuth();
        if (!isValid) {
          setErrorReviews('Sessão expirada. Faça login novamente.');
        } else {
          setErrorReviews('Erro ao carregar avaliações');
        }
      } else {
        setErrorReviews('Erro ao carregar avaliações');
      }
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      setErrorProducts(null);
      const data = await dashboardService.getPopularProducts(placeId, 5);
      setPopularProducts(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Se for 401, validar a autenticação
        const isValid = await validateAuth();
        if (!isValid) {
          setErrorProducts('Sessão expirada. Faça login novamente.');
        } else {
          setErrorProducts('Erro ao carregar produtos');
        }
      } else {
        setErrorProducts('Erro ao carregar produtos');
      }
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (placeId) {
      // Carregar dados em paralelo
      fetchStats();
      fetchReviews();
      fetchProducts();
    }
  }, [placeId]);

  return {
    stats,
    recentReviews,
    popularProducts,
    loadingStats,
    loadingReviews,
    loadingProducts,
    errorStats,
    errorReviews,
    errorProducts,
    refetchStats: fetchStats,
    refetchReviews: fetchReviews,
    refetchProducts: fetchProducts,
  };
}; 